import classNames from 'classnames';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {BlockList} from '@udemy/react-core-components';
import {ShowMore} from '@udemy/react-reveal-components';
import {CarouselWithFocusOrder} from '@udemy/react-structure-components';

import styles from './carousel-list-layout.module.less';

type BlockListProps = React.ComponentProps<typeof BlockList>;

export enum LayoutVariant {
    Desktop = 'desktop',
    Mobile = 'mobile',
}

export type CarouselListLayoutProps<ItemType> = {
    collapsedItemsCount: number;
    items: ItemType[];
    renderItem: (item: ItemType, index: number, variant: LayoutVariant) => React.ReactNode;
    totalItemsCount: number;
    blockListSize?: BlockListProps['size'];
    className?: string;
    collapsedHeightFallback?: number;
    desktopBreakpoint?: Parameters<typeof useMatchMedia>[0];
};

export function CarouselListLayout<ItemType>({
    collapsedItemsCount,
    items,
    renderItem,
    totalItemsCount,
    blockListSize = 'large',
    className,
    collapsedHeightFallback = 179,
    desktopBreakpoint = 'md-min',
}: CarouselListLayoutProps<ItemType>) {
    const {gettext, interpolate} = useI18n();
    const isDesktop = useMatchMedia(desktopBreakpoint);
    const measurementContainerRef = useRef<HTMLDivElement>(null);
    const [collapsedHeight, setCollapsedHeight] = useState<number>(collapsedHeightFallback);

    const wrapperClassName = classNames(className, styles.wrapper);
    const showMoreCount = Math.max(0, totalItemsCount - collapsedItemsCount);
    const shouldShowMore = showMoreCount > 0;
    const measurementIndex = Math.min(collapsedItemsCount, items.length) - 1;

    const mobileItems = useMemo(
        () =>
            items.map((item, index) => {
                const shouldMeasure = index === measurementIndex;

                return (
                    <BlockList.Item
                        key={index}
                        ref={shouldMeasure ? measurementContainerRef : undefined}
                        className={styles['block-list-item']}
                    >
                        {renderItem(item, index, LayoutVariant.Mobile)}
                    </BlockList.Item>
                );
            }),
        [items, measurementIndex, renderItem],
    );

    useEffect(() => {
        if (isDesktop || !measurementContainerRef.current) {
            return;
        }

        const measureHeight = () => {
            const container = measurementContainerRef.current;
            if (container) {
                const height = container.offsetTop + container.offsetHeight;
                if (height > 0) {
                    setCollapsedHeight(Math.ceil(height));
                }
            }
        };

        measureHeight();

        const resizeObserver = new ResizeObserver(measureHeight);
        resizeObserver.observe(measurementContainerRef.current);

        return () => resizeObserver.disconnect();
    }, [items.length, collapsedItemsCount, isDesktop, measurementIndex]);

    if (isDesktop) {
        return (
            <CarouselWithFocusOrder className={wrapperClassName}>
                {items.map((item, index) => renderItem(item, index, LayoutVariant.Desktop))}
            </CarouselWithFocusOrder>
        );
    }

    return shouldShowMore ? (
        <ShowMore
            contentClassName={classNames(styles['show-more-content'], wrapperClassName)}
            fullWidthButton
            collapsedHeight={collapsedHeight}
            moreButtonLabel={interpolate(gettext('Show %s more'), [showMoreCount])}
        >
            <BlockList size={blockListSize}>{mobileItems}</BlockList>
        </ShowMore>
    ) : (
        <BlockList className={wrapperClassName} size={blockListSize}>
            {mobileItems}
        </BlockList>
    );
}
